import naja from 'naja';
import MicroModal from 'micromodal';
import PackageSelect from './PackageSelect';
import CustomSelect from './CustomSelect';
import NiceSelect from './NiceSelect';

export default class OrderConfiguration {
    private form: HTMLFormElement | null = null;

    constructor() {
        this.initializeForm();
        this.initSnippetListener();
    }

    static init(): void {
        const form = document.getElementById('frm-orderConfiguration-configurationForm') as HTMLFormElement;
        if (form) {
            new OrderConfiguration();
        }
    }

    private initializeForm(): void {
        this.form = document.getElementById('frm-orderConfiguration-configurationForm') as HTMLFormElement;

        const openButtons = this.form.querySelectorAll('[data-micromodal-trigger]');
        openButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const modalId = button.getAttribute('data-micromodal-trigger');
                if (modalId) {
                    MicroModal.show(modalId);
                }
            });
        });

        if (!this.form) {
            return;
        }

        this.bindEvents();
        this.bindPackageNameEditing();
        NiceSelect.reinitialize();
    }

    private bindEvents(): void {
        if (!this.form) {
            return;
        }

        const radioInputs = this.form.querySelectorAll<HTMLInputElement>('input[type="radio"]');
        const submitBtn = this.form.querySelector<HTMLInputElement>('input[type="submit"]');

        radioInputs.forEach((radio) => {
            radio.addEventListener('change', () => {
                if (submitBtn) {
                    submitBtn.click();
                }
            });
        });
    }


    private initSnippetListener(): void {
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (event.detail.snippet.id === 'snippet-orderConfiguration-orderConfiguration') {
                // Close any open modals and cleanup scroll lock before reinitializing
                MicroModal.close('cart-modal');
                // Ensure body overflow is reset even if modal close didn't work properly
                document.body.style.overflow = '';
                this.initializeForm();
            }
        });
    }

    private bindPackageNameEditing(): void {
        const editButtons = document.querySelectorAll('.package-edit-btn');

        editButtons.forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const packageId = button.getAttribute('data-package-id');
                if (packageId) {
                    this.startPackageNameEdit(parseInt(packageId));
                }
            });
        });
    }

    private startPackageNameEdit(packageId: number): void {
        const packageNameSpan = document.querySelector(`.package-name[data-package-id="${packageId}"]`) as HTMLElement;
        const editButton = document.querySelector(`.package-edit-btn[data-package-id="${packageId}"]`) as HTMLElement;

        if (!packageNameSpan || !editButton) {
            return;
        }

        const currentName = packageNameSpan.textContent || '';

        // Create input element
        const input = document.createElement('input');
        input.type = 'text';
        input.value = currentName;
        input.className = 'package-name-input';
        input.style.cssText =
            'border: 1px solid #ccc; padding: 2px 5px; font-size: inherit; font-weight: inherit; background: white; width: 200px;';

        // Replace span with input
        packageNameSpan.style.display = 'none';
        editButton.style.display = 'none';
        packageNameSpan.parentNode?.insertBefore(input, packageNameSpan);

        // Focus input and select text
        input.focus();
        input.select();

        // Handle save/cancel
        const saveEdit = () => {
            const newName = input.value.trim();
            if (newName && newName !== currentName) {
                this.savePackageName(packageId, newName);
            } else {
                this.cancelPackageNameEdit(packageId);
            }
        };

        const cancelEdit = () => {
            this.cancelPackageNameEdit(packageId);
        };

        // Event listeners
        input.addEventListener('blur', saveEdit);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                saveEdit();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
            }
        });
    }

    private savePackageName(packageId: number, name: string): void {
        // Get the URL template from the button's data attribute (generated by Latte {link})
        const editButton = document.querySelector(`.package-edit-btn[data-package-id="${packageId}"]`) as HTMLElement;
        if (!editButton) {
            console.error('Edit button not found for package', packageId);
            this.cancelPackageNameEdit(packageId);
            return;
        }

        const urlTemplate = editButton.getAttribute('data-rename-url');
        if (!urlTemplate) {
            console.error('Rename URL not found for package', packageId);
            this.cancelPackageNameEdit(packageId);
            return;
        }

        // Replace the placeholder with the actual name
        const url = urlTemplate.replace('PLACEHOLDER_NAME', encodeURIComponent(name));

        naja.makeRequest('GET', url, null, {
            history: false,
        }).catch((error) => {
            console.error('Failed to save package name:', error);
            this.cancelPackageNameEdit(packageId);
        });
    }

    private cancelPackageNameEdit(packageId: number): void {
        const input = document.querySelector(`.package-name-input`);
        const packageNameSpan = document.querySelector(`.package-name[data-package-id="${packageId}"]`) as HTMLElement;
        const editButton = document.querySelector(`.package-edit-btn[data-package-id="${packageId}"]`) as HTMLElement;

        if (input) {
            input.remove();
        }

        if (packageNameSpan) {
            packageNameSpan.style.display = '';
        }

        if (editButton) {
            editButton.style.display = '';
        }
    }
}
